/* global Word */
/**
 * Esqueleto do documento: o PLANO de tópicos que o usuário define antes de
 * conversar (tela intermediária) e que o modelo recebe no contexto de todo
 * turno.
 *
 * Onde vive: numa **propriedade personalizada do documento** (`miau.skeleton`,
 * WordApi 1.3 — o manifest já exige 1.4). É metadado do DOCUMENTO, não da
 * conversa: sobrevive a "Nova conversa", a recarregar o painel e a fechar e
 * reabrir o arquivo. O painel mantém uma cópia em memória como fonte de
 * trabalho; esta camada é a persistência.
 *
 * Por que não no backend: ele é stateless por decisão de arquitetura
 * (ver CLAUDE.md). Por que não só em memória: o usuário pode fechar o painel no
 * meio do trabalho, e o plano é do arquivo, não da sessão.
 *
 * Tudo aqui é **best-effort**: um host sem a API, ou um documento que não aceite
 * a propriedade, não pode impedir o usuário de escrever. Em falha, o plano segue
 * valendo em memória durante a sessão.
 */
/** Nome da propriedade personalizada no documento. */
const SKELETON_PROPERTY = 'miau.skeleton';
/** Teto de tópicos. Um plano é um sumário, não o documento inteiro. */
export const DEFAULT_SKELETON_MAX_TOPICS = 40;
/** Teto por tópico: título de seção, não parágrafo. */
export const SKELETON_TOPIC_MAX_CHARS = 120;
/**
 * Mínimo de tópicos para CONFIRMAR um plano. Abaixo disso o plano não orienta
 * nada (um ou dois tópicos não são uma estrutura), então a tela bloqueia o
 * Confirmar. Não vale para o "Pular": seguir sem plano é uma escolha legítima e
 * distinta de confirmar um plano incompleto.
 */
export const SKELETON_MIN_TOPICS = 3;
/**
 * Normaliza uma lista de tópicos vinda de qualquer origem (UI, documento,
 * ferramenta do modelo): descarta o que não é texto, tira espaço em volta,
 * remove numeração que o autor tenha digitado (a numeração é derivada da
 * POSIÇÃO — ver `renderSkeletonEditor`), corta vazios e aplica os tetos.
 */
export function normalizeSkeleton(topics) {
    if (!Array.isArray(topics))
        return [];
    return topics
        .filter((t) => typeof t === 'string')
        .map((t) => t
        .replace(/\s+/g, ' ')
        .replace(/^\s*\d+(?:\.\d+)*\.?[\s)-]*/, '') // "3." / "3.1" / "3 -" digitados
        .trim()
        .slice(0, SKELETON_TOPIC_MAX_CHARS))
        .filter((t) => t.length > 0)
        .slice(0, DEFAULT_SKELETON_MAX_TOPICS);
}
/**
 * Plano vigente na sessão. O estado vive AQUI (e não no taskpane) porque quem
 * precisa dele são dois lados que não podem se importar em cadeia: a UI, que
 * edita, e a ferramenta `set_document_skeleton` em tools.ts, que o modelo usa.
 * Com o estado no módulo do domínio, ninguém depende do taskpane e não há ciclo.
 */
let currentSkeleton = [];
/** Plano vigente (contexto do turno e ferramenta). Vazio = sem plano. */
export function getSkeleton() {
    return currentSkeleton;
}
/**
 * Carrega no estado o plano que o DOCUMENTO já traz, sem regravar. Usado ao
 * escolher o tipo: se o arquivo foi reaberto, o plano é dele — o painel só
 * herda. Vale para qualquer tipo, inclusive os que não têm a tela do esqueleto.
 */
export async function hydrateSkeleton() {
    currentSkeleton = await readSkeletonFromDocument();
    return currentSkeleton;
}
/**
 * Define o plano vigente e persiste no documento (best-effort). Devolve a lista
 * normalizada — é ela que vale, não a que entrou.
 */
export async function setSkeleton(topics) {
    currentSkeleton = normalizeSkeleton(topics);
    await saveSkeletonToDocument(currentSkeleton);
    return currentSkeleton;
}
/** Lê o esqueleto guardado no documento. Lista vazia = não há plano. */
export async function readSkeletonFromDocument() {
    try {
        return await Word.run(async (context) => {
            const prop = context.document.properties.customProperties.getItemOrNullObject(SKELETON_PROPERTY);
            prop.load('value,isNullObject');
            await context.sync();
            if (prop.isNullObject)
                return [];
            const raw = typeof prop.value === 'string' ? prop.value : '';
            if (!raw.trim())
                return [];
            return normalizeSkeleton(JSON.parse(raw));
        });
    }
    catch (e) {
        console.warn('[skeleton] não consegui ler o esqueleto do documento:', e);
        return [];
    }
}
/**
 * Grava o esqueleto no documento. Lista vazia REMOVE a propriedade — "sem
 * plano" é um estado legítimo (o usuário pode apagar todos os tópicos), e
 * guardar `[]` faria o contexto do turno afirmar que existe um plano vazio.
 */
export async function saveSkeletonToDocument(topics) {
    const clean = normalizeSkeleton(topics);
    try {
        return await Word.run(async (context) => {
            const props = context.document.properties.customProperties;
            if (clean.length === 0) {
                const existing = props.getItemOrNullObject(SKELETON_PROPERTY);
                existing.load('isNullObject');
                await context.sync();
                if (!existing.isNullObject) {
                    existing.delete();
                    await context.sync();
                }
                return true;
            }
            // add() sobrescreve quando a chave já existe (é o comportamento do
            // Office.js para customProperties), então não precisa apagar antes.
            props.add(SKELETON_PROPERTY, JSON.stringify(clean));
            await context.sync();
            return true;
        });
    }
    catch (e) {
        // Não é fatal: o plano continua em memória e no contexto dos turnos desta
        // sessão; só não sobrevive a fechar o painel.
        console.warn('[skeleton] não consegui gravar o esqueleto no documento:', e);
        return false;
    }
}
/**
 * Bloco do esqueleto para o contexto do turno. Vazio ⇒ string vazia (nenhuma
 * linha entra no prompt).
 *
 * O enquadramento é deliberadamente FIRME quanto a nome e ordem. A primeira
 * versão dizia só "plano de tópicos, não contrato" e o modelo trocava o nome do
 * tópico pelo título de exemplo do manual do tipo — pedir "escreva o começo" com
 * "Visão Geral" no plano rendia um "1. Contextualização", que é um dos exemplos
 * da norma. Os nomes do usuário têm precedência sobre os exemplos do manual; o
 * que continua livre é o usuário mudar o plano quando PEDIR.
 */
export function formatSkeletonBlock() {
    const clean = currentSkeleton;
    if (!clean.length)
        return '';
    const list = clean.map((t, i) => `  ${i + 1}. ${t}`).join('\n');
    // SÓ A LISTA. As regras de como seguir o esqueleto (nomes literais, precedência
    // sobre o manual, numeração pela posição, quem pode mudar o plano) vivem na
    // seção "Esqueleto" do system prompt — que é CACHEADO. Este bloco viaja dentro
    // de `messages` e é reenviado a cada passo do turno, sem cache: repetir as
    // regras aqui custava ~300 tokens por passo para dizer o que o modelo já leu.
    return ('\nEsqueleto CONFIRMADO pelo usuário (plano de tópicos deste documento):\n' +
        `${list}\n`);
}
